import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  FileText, Plus, Eye, Download, Send, Calendar, Clock,
  User, Building2, AlertCircle, CheckCircle2, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const COMPLAINT_CATEGORIES = [
  { id: 'repairs', label: 'Repairs & Maintenance', description: 'Issues with property repairs or maintenance' },
  { id: 'deposits', label: 'Deposit Protection', description: 'Disputes over deposit returns or protection' },
  { id: 'rent', label: 'Rent Issues', description: 'Rent increases, arrears, or payment disputes' },
  { id: 'harassment', label: 'Harassment or Discrimination', description: 'Landlord/tenant harassment claims' },
  { id: 'safety', label: 'Health & Safety', description: 'Gas, electrical, or fire safety concerns' },
  { id: 'eviction', label: 'Eviction Disputes', description: 'Issues with eviction notices or procedures' },
  { id: 'terms', label: 'Tenancy Terms', description: 'Disputes over tenancy agreement terms' },
  { id: 'privacy', label: 'Privacy Violations', description: 'Unauthorized entry or privacy concerns' },
  { id: 'utilities', label: 'Utilities & Services', description: 'Issues with utility billing or services' },
  { id: 'other', label: 'Other', description: 'Other complaints not listed above' }
];

export default function OmbudsmanCasesPage() {
  const [casePacks, setCasePacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    tenancy_id: '',
    property_id: '',
    complainant_name: '',
    complainant_role: 'tenant',
    respondent_name: '',
    respondent_role: 'landlord',
    complaint_category: '',
    complaint_summary: '',
    desired_outcome: '',
    incident_date: '',
    internal_resolution_attempted: false,
    internal_resolution_outcome: ''
  });

  useEffect(() => {
    fetchCasePacks();
  }, []);

  const fetchCasePacks = async () => {
    try {
      const response = await api.get('/prs-services/ombudsman/case-packs');
      setCasePacks(response.data.case_packs);
    } catch (error) {
      toast.error('Failed to load case packs');
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseDetails = async (caseId) => {
    try {
      const response = await api.get(`/prs-services/ombudsman/case-packs/${caseId}`);
      setSelectedCase(response.data.case_pack);
    } catch (error) {
      toast.error('Failed to load case details');
    }
  };

  const handleCreateCase = async (e) => {
    e.preventDefault();
    try {
      await api.post('/prs-services/ombudsman/case-packs', formData);
      toast.success('Case pack created successfully');
      setShowCreateForm(false);
      fetchCasePacks();
    } catch (error) {
      toast.error('Failed to create case pack');
    }
  };

  const handleGeneratePack = async (caseId) => {
    try {
      await api.post(`/prs-services/ombudsman/case-packs/${caseId}/generate`);
      toast.success('Case pack document generated');
      fetchCaseDetails(caseId);
    } catch (error) {
      toast.error('Failed to generate pack');
    }
  };

  const handleSubmitToOmbudsman = async (caseId) => {
    try {
      await api.post('/prs-services/ombudsman/case-packs/submit', { case_pack_id: caseId });
      toast.success('Case submitted to ombudsman');
      fetchCasePacks();
    } catch (error) {
      toast.error('Failed to submit case');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      submitted: 'bg-blue-100 text-blue-800',
      acknowledged: 'bg-indigo-100 text-indigo-800',
      under_review: 'bg-yellow-100 text-yellow-800',
      mediation: 'bg-purple-100 text-purple-800',
      decision_pending: 'bg-orange-100 text-orange-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-slate-100 text-slate-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status.replace('_', ' ')}</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="ombudsman-cases-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Ombudsman Case Packs</h1>
            <p className="text-slate-600">Prepare and manage dispute resolution documentation</p>
          </div>
          <Button onClick={() => setShowCreateForm(true)} data-testid="create-case-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Case Pack
          </Button>
        </div>

        {showCreateForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Create Ombudsman Case Pack</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateCase} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    placeholder="Tenancy ID"
                    value={formData.tenancy_id}
                    onChange={(e) => setFormData({...formData, tenancy_id: e.target.value})}
                    required
                  />
                  <Input
                    placeholder="Property ID"
                    value={formData.property_id}
                    onChange={(e) => setFormData({...formData, property_id: e.target.value})}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Complainant</label>
                    <Input
                      placeholder="Complainant Name"
                      value={formData.complainant_name}
                      onChange={(e) => setFormData({...formData, complainant_name: e.target.value})}
                      required
                    />
                    <select
                      className="mt-2 w-full border rounded-md p-2"
                      value={formData.complainant_role}
                      onChange={(e) => setFormData({...formData, complainant_role: e.target.value})}
                    >
                      <option value="tenant">Tenant</option>
                      <option value="landlord">Landlord</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Respondent</label>
                    <Input
                      placeholder="Respondent Name"
                      value={formData.respondent_name}
                      onChange={(e) => setFormData({...formData, respondent_name: e.target.value})}
                      required
                    />
                    <select
                      className="mt-2 w-full border rounded-md p-2"
                      value={formData.respondent_role}
                      onChange={(e) => setFormData({...formData, respondent_role: e.target.value})}
                    >
                      <option value="landlord">Landlord</option>
                      <option value="tenant">Tenant</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Complaint Category</label>
                  <select
                    className="w-full border rounded-md p-2"
                    value={formData.complaint_category}
                    onChange={(e) => setFormData({...formData, complaint_category: e.target.value})}
                    required
                  >
                    <option value="">Select category...</option>
                    {COMPLAINT_CATEGORIES.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <Textarea
                  placeholder="Complaint Summary - Describe the issue in detail..."
                  value={formData.complaint_summary}
                  onChange={(e) => setFormData({...formData, complaint_summary: e.target.value})}
                  rows={4}
                  required
                />

                <Textarea
                  placeholder="Desired Outcome - What resolution are you seeking?"
                  value={formData.desired_outcome}
                  onChange={(e) => setFormData({...formData, desired_outcome: e.target.value})}
                  rows={2}
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    type="date"
                    placeholder="Incident Date"
                    value={formData.incident_date}
                    onChange={(e) => setFormData({...formData, incident_date: e.target.value})}
                  />
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.internal_resolution_attempted}
                      onChange={(e) => setFormData({...formData, internal_resolution_attempted: e.target.checked})}
                    />
                    <span>Internal resolution attempted</span>
                  </label>
                </div>

                <div className="flex gap-2">
                  <Button type="submit">Create Case Pack</Button>
                  <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Case Packs
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : casePacks.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No case packs yet. Create one to prepare for ombudsman submission.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {casePacks.map((pack) => (
                      <div
                        key={pack.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedCase?.id === pack.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-slate-50'
                        }`}
                        onClick={() => fetchCaseDetails(pack.id)}
                        data-testid={`case-pack-${pack.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{pack.pack_reference}</h3>
                              {getStatusBadge(pack.status)}
                            </div>
                            <p className="text-sm text-slate-600 mt-1">
                              {COMPLAINT_CATEGORIES.find(c => c.id === pack.complaint_category)?.label || pack.complaint_category}
                            </p>
                            <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                              {pack.complaint_summary}
                            </p>
                          </div>
                          <ChevronRight className="h-5 w-5 text-slate-400" />
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {pack.complainant_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(pack.complaint_raised_date).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div>
            {selectedCase ? (
              <Card>
                <CardHeader>
                  <CardTitle>Case Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Reference</p>
                    <p className="font-mono">{selectedCase.pack_reference}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Status</p>
                    {getStatusBadge(selectedCase.status)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Complainant</p>
                    <p>{selectedCase.complainant_name} ({selectedCase.complainant_role})</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Respondent</p>
                    <p>{selectedCase.respondent_name} ({selectedCase.respondent_role})</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Summary</p>
                    <p className="text-sm">{selectedCase.complaint_summary}</p>
                  </div>

                  <div className="border-t pt-4 space-y-2">
                    {selectedCase.status === 'draft' && (
                      <>
                        <Button className="w-full" onClick={() => handleGeneratePack(selectedCase.id)}>
                          <FileText className="h-4 w-4 mr-2" />
                          Generate Pack Document
                        </Button>
                        {selectedCase.pack_generated && (
                          <Button className="w-full" variant="outline" onClick={() => handleSubmitToOmbudsman(selectedCase.id)}>
                            <Send className="h-4 w-4 mr-2" />
                            Submit to Ombudsman
                          </Button>
                        )}
                      </>
                    )}
                    {selectedCase.pack_file_url && (
                      <Button variant="outline" className="w-full" asChild>
                        <a href={selectedCase.pack_file_url} download>
                          <Download className="h-4 w-4 mr-2" />
                          Download Pack
                        </a>
                      </Button>
                    )}
                  </div>

                  {selectedCase.timeline && selectedCase.timeline.length > 0 && (
                    <div className="border-t pt-4">
                      <p className="text-sm font-medium text-slate-500 mb-2">Timeline</p>
                      <div className="space-y-2">
                        {selectedCase.timeline.map((entry, idx) => (
                          <div key={idx} className="text-sm border-l-2 border-slate-200 pl-3">
                            <p className="font-medium">{entry.event_title}</p>
                            <p className="text-slate-500 text-xs">{new Date(entry.event_date).toLocaleDateString()}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-slate-500">
                  Select a case pack to view details
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
